import { useTranslation } from "react-i18next";
import { Eye, MapPin, Clock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

/**
 * Sidebar del Geoportal — reorganizada en 3 grupos claros (Fase 2).
 *   ¿Qué ves?  → capas
 *   ¿Dónde?    → concello + basemap
 *   ¿Cuándo?   → rango temporal + toggle "sin actividad reciente"
 * Cita cliente: "hazlo como para niños pequeños".
 */
export default function GeoFilters({
  concellos,
  filters,
  onFiltersChange,
  layers,
  onLayersChange,
  basemap,
  onBasemapChange,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";

  const setFilter = (k, v) => onFiltersChange({ ...filters, [k]: v });
  const setLayer = (k, v) => onLayersChange({ ...layers, [k]: v });

  return (
    <aside
      className="w-full lg:w-80 shrink-0 border-r hairline bg-white overflow-y-auto"
      data-testid="geo-filters"
    >
      <div className="p-6 space-y-6">
        {/* ¿Qué ves? — capas */}
        <Group
          icon={Eye}
          title={lang === "es" ? "¿Qué ves?" : "¿Que ves?"}
          testid="geo-filters-group-what"
        >
          <ToggleRow
            testid="layer-detections"
            label={t("fase2.layer_detections")}
            checked={layers.detections}
            onChange={(v) => setLayer("detections", v)}
          />
          <ToggleRow
            testid="layer-overflow"
            label={t("fase2.layer_overflow")}
            checked={layers.overflow}
            onChange={(v) => setLayer("overflow", v)}
          />
          <ToggleRow
            testid="layer-observations"
            label={t("fase2.layer_observations")}
            checked={layers.observations}
            onChange={(v) => setLayer("observations", v)}
          />
          <ToggleRow
            testid="layer-vehicles"
            label={t("fase2.layer_vehicles")}
            checked={layers.vehicles}
            onChange={(v) => setLayer("vehicles", v)}
          />
          <ToggleRow
            testid="layer-concellos"
            label={t("fase2.layer_concellos")}
            checked={layers.concellos}
            onChange={(v) => setLayer("concellos", v)}
          />
          <ToggleRow
            testid="layer-sigeus-points"
            label={t("fase2.layer_sigeus_points")}
            checked={!!layers.sigeus_points}
            onChange={(v) => setLayer("sigeus_points", v)}
          />
          <ToggleRow
            testid="layer-weighins"
            label={lang === "es" ? "Pesajes" : "Pesaxes"}
            checked={!!layers.weighins}
            onChange={(v) => setLayer("weighins", v)}
          />
          {layers.weighins && (
            <div
              className="ml-6 flex items-center justify-between rounded-lg bg-salnes-sand/40 px-3 py-2"
              data-testid="layer-weighins-events-row"
            >
              <div className="flex flex-col">
                <Label className="text-xs text-salnes-ink font-normal cursor-pointer">
                  {lang === "es"
                    ? "Ver eventos individuales"
                    : "Ver eventos individuais"}
                </Label>
                <span className="text-[10px] text-salnes-stone">
                  {lang === "es"
                    ? "Detalle histórico · puede ralentizar"
                    : "Detalle histórico · pode ralentizar"}
                </span>
              </div>
              <Switch
                checked={!!filters.weighins_mode_events}
                onCheckedChange={(v) => setFilter("weighins_mode_events", v)}
                data-testid="layer-weighins-events-toggle"
              />
            </div>
          )}
        </Group>

        {/* ¿Dónde? — concello + basemap */}
        <Group
          icon={MapPin}
          title={lang === "es" ? "¿Dónde?" : "Onde?"}
          testid="geo-filters-group-where"
        >
          <Field label={t("fase2.concello")}>
            <Select
              value={filters.concello || "all"}
              onValueChange={(v) => setFilter("concello", v)}
            >
              <SelectTrigger className="h-10" data-testid="filter-concello">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("fase2.all_concellos")}</SelectItem>
                {concellos.map((c) => (
                  <SelectItem key={c.key} value={c.key}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("fase2.basemap")}>
            <Select value={basemap} onValueChange={onBasemapChange}>
              <SelectTrigger className="h-10" data-testid="filter-basemap">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="carto">{t("fase2.basemap_carto")}</SelectItem>
                <SelectItem value="pnoa" data-testid="basemap-option-pnoa">
                  {lang === "es" ? "Ortofoto (PNOA · IGN)" : "Ortofoto (PNOA · IGN)"}
                </SelectItem>
                <SelectItem value="esri">{t("fase2.basemap_esri")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </Group>

        {/* ¿Cuándo? — rango + inactive toggle */}
        <Group
          icon={Clock}
          title={lang === "es" ? "¿Cuándo?" : "Cando?"}
          testid="geo-filters-group-when"
        >
          <Field label={t("fase2.date_range")}>
            <Select
              value={filters.range}
              onValueChange={(v) => setFilter("range", v)}
            >
              <SelectTrigger className="h-10" data-testid="filter-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">{t("fase2.last_24h")}</SelectItem>
                <SelectItem value="7d">{t("fase2.last_7d")}</SelectItem>
                <SelectItem value="30d">{t("fase2.last_30d")}</SelectItem>
                <SelectItem value="all">
                  {lang === "es" ? "Todo" : "Todo"}
                </SelectItem>
                <SelectItem value="custom" data-testid="range-option-custom">
                  {lang === "es" ? "Rango personalizado" : "Rango personalizado"}
                </SelectItem>
                <SelectItem value="day" data-testid="range-option-day">
                  {lang === "es" ? "Día específico" : "Día específico"}
                </SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {filters.range === "day" && (
            <div data-testid="range-day-picker">
              <Field label={lang === "es" ? "Selecciona el día" : "Selecciona o día"}>
                <input
                  type="date"
                  value={filters.date || ""}
                  onChange={(e) => setFilter("date", e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  data-testid="range-day-input"
                  className="h-10 w-full rounded-md border border-salnes-stone/30 bg-white px-3 text-sm text-salnes-ink focus-visible:outline-2 focus-visible:outline-salnes-atlantic"
                />
              </Field>
            </div>
          )}
          {filters.range === "custom" && (
            <div
              className="grid grid-cols-2 gap-2"
              data-testid="range-custom-pickers"
            >
              <Field label={lang === "es" ? "Desde" : "Dende"}>
                <input
                  type="date"
                  value={filters.from || ""}
                  onChange={(e) => setFilter("from", e.target.value)}
                  max={filters.to || undefined}
                  data-testid="range-custom-from"
                  className="h-10 w-full rounded-md border border-salnes-stone/30 bg-white px-3 text-sm text-salnes-ink focus-visible:outline-2 focus-visible:outline-salnes-atlantic"
                />
              </Field>
              <Field label={lang === "es" ? "Hasta" : "Ata"}>
                <input
                  type="date"
                  value={filters.to || ""}
                  onChange={(e) => setFilter("to", e.target.value)}
                  min={filters.from || undefined}
                  data-testid="range-custom-to"
                  className="h-10 w-full rounded-md border border-salnes-stone/30 bg-white px-3 text-sm text-salnes-ink focus-visible:outline-2 focus-visible:outline-salnes-atlantic"
                />
              </Field>
              {filters.from && filters.to && filters.from > filters.to && (
                <div
                  className="col-span-2 text-[10px] text-salnes-coral"
                  data-testid="range-custom-error"
                >
                  {lang === "es"
                    ? "«Desde» no puede ser posterior a «Hasta»."
                    : "«Dende» non pode ser posterior a «Ata»."}
                </div>
              )}
            </div>
          )}
          <Field label={t("fase2.show")}>
            <Select
              value={filters.show}
              onValueChange={(v) => setFilter("show", v)}
            >
              <SelectTrigger className="h-10" data-testid="filter-show">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("fase2.show_all")}</SelectItem>
                <SelectItem value="overflow">{t("fase2.show_overflow")}</SelectItem>
                <SelectItem value="observations">
                  {t("fase2.show_observations")}
                </SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <ToggleRow
            testid="filter-include-inactive"
            label={
              lang === "es"
                ? "Mostrar también puntos sin actividad reciente"
                : "Mostrar tamén puntos sen actividade recente"
            }
            checked={!!filters.include_inactive}
            onChange={(v) => setFilter("include_inactive", v)}
            hint={
              lang === "es"
                ? "Contenedores sin pesajes en 90 días. Apagado por defecto."
                : "Contedores sen pesaxes en 90 días. Apagado por defecto."
            }
          />
        </Group>
      </div>
    </aside>
  );
}

function Group({ icon: Icon, title, children, testid }) {
  return (
    <section
      className="rounded-2xl border border-salnes-stone/10 p-5 bg-salnes-ivory/30"
      data-testid={testid}
    >
      <header className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-salnes-atlantic/10 text-salnes-atlantic flex items-center justify-center">
          <Icon size={16} strokeWidth={2} />
        </div>
        <h3 className="font-serif text-lg text-salnes-ink leading-none">
          {title}
        </h3>
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] text-salnes-stone">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid, hint }) {
  return (
    <div className="flex flex-col gap-1 py-1">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-salnes-ink/85 leading-tight flex-1">{label}</span>
        <Switch
          checked={checked}
          onCheckedChange={onChange}
          data-testid={testid}
        />
      </div>
      {hint && (
        <div className="text-[10px] text-salnes-stone/70 leading-snug">
          {hint}
        </div>
      )}
    </div>
  );
}
